import Service, { service } from '@ember/service';

/**
 * Data Merger Service
 *
 * Handles loading and merging of REST API data into the event data store.
 * Implements merge lock pattern to prevent WebSocket/REST race conditions.
 */
export default class DataMergerService extends Service {
  @service eventData;

  // Merge lock: queues WebSocket messages during REST data merge to prevent race conditions
  // Map of eventId -> { locked: boolean, pendingMessages: [] }
  _mergeLocks = new Map();

  /**
   * Acquire merge lock for an event - queues WebSocket messages during REST merge
   */
  acquireMergeLock(eventId) {
    this._mergeLocks.set(eventId, { locked: true, pendingMessages: [] });
    console.log(`[DataMerger] Acquired merge lock for event ${eventId}`);
  }

  /**
   * Release merge lock and return any queued messages for processing
   */
  releaseMergeLock(eventId) {
    const lock = this._mergeLocks.get(eventId);
    if (!lock) return [];

    const pendingCount = lock.pendingMessages.length;
    const pendingMessages = lock.pendingMessages;
    console.log(`[DataMerger] Releasing merge lock for event ${eventId}, ${pendingCount} queued messages`);

    // Clear the lock
    this._mergeLocks.delete(eventId);

    return pendingMessages;
  }

  /**
   * Check if merge is locked for an event
   */
  isMergeLocked(eventId) {
    const lock = this._mergeLocks.get(eventId);
    return lock?.locked === true;
  }

  /**
   * Queue a message during merge lock
   */
  queueMessage(eventId, message) {
    const lock = this._mergeLocks.get(eventId);
    if (lock) {
      lock.pendingMessages.push(message);
      console.log(`[DataMerger] Queued message during merge (${lock.pendingMessages.length} queued): ${message.messageType || message.type}`);
    }
  }

  /**
   * Load initial reference data from REST API response
   * All data uses uuid as keys
   */
  loadReferenceData(eventId, referenceData) {
    const eventDataObj = this.eventData.events.get(eventId);
    if (!eventDataObj) {
      console.warn(`[DataMerger] Cannot load reference data: event ${eventId} not initialized`);
      return;
    }

    console.log(`[DataMerger] Loading initial reference data for event ${eventId}`);

    // Clear existing reference data first (important for page refresh to get fresh state)
    eventDataObj.classGroups.clear();
    eventDataObj.classes.clear();
    eventDataObj.entries.clear();
    eventDataObj.courses.clear();
    eventDataObj.tieTimingRecords.clear();
    eventDataObj.penaltyTimingRecords.clear();

    // Load event
    if (referenceData.event) {
      eventDataObj.event = referenceData.event;
    }
    eventDataObj.snapshotProgress.event = true;

    // Load classGroups (keyed by uuid)
    if (referenceData.classGroups && referenceData.classGroups.length > 0) {
      referenceData.classGroups.forEach(cg => eventDataObj.classGroups.set(cg.uuid, cg));
    }
    eventDataObj.snapshotProgress.classgroups = true;

    // Load classes (keyed by uuid)
    if (referenceData.classes && referenceData.classes.length > 0) {
      referenceData.classes.forEach(cls => eventDataObj.classes.set(cls.uuid, cls));
    }
    eventDataObj.snapshotProgress.classes = true;

    // Load entries (keyed by uuid)
    if (referenceData.entries && referenceData.entries.length > 0) {
      referenceData.entries.forEach(entry => eventDataObj.entries.set(entry.uuid, entry));
    }
    eventDataObj.snapshotProgress.entries = true;

    // Load courses (keyed by uuid)
    if (referenceData.courses && referenceData.courses.length > 0) {
      referenceData.courses.forEach(course => eventDataObj.courses.set(course.uuid, course));
    }
    eventDataObj.snapshotProgress.courses = true;

    // Load tieTimingRecords (keyed by entryUuid)
    if (referenceData.tieTimingRecords && referenceData.tieTimingRecords.length > 0) {
      referenceData.tieTimingRecords.forEach(tie => eventDataObj.tieTimingRecords.set(tie.entryUuid, tie));
    }
    eventDataObj.snapshotProgress.tietimingrecords = true;

    // Load penaltyTimingRecords (keyed by entryUuid)
    if (referenceData.penaltyTimingRecords && referenceData.penaltyTimingRecords.length > 0) {
      referenceData.penaltyTimingRecords.forEach(penalty => {
        const entryUuid = penalty.entryUuid;
        const existing = eventDataObj.penaltyTimingRecords.get(entryUuid) || [];
        // Create new array for reactivity
        eventDataObj.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
      });
    }
    eventDataObj.snapshotProgress.penaltytimingrecords = true;

    console.log(`[DataMerger] Reference data loaded:`, {
      classGroups: eventDataObj.classGroups.size,
      classes: eventDataObj.classes.size,
      entries: eventDataObj.entries.size,
      courses: eventDataObj.courses.size
    });
  }

  /**
   * Load initial timing data from REST API response
   * Timing records are keyed by entryUuid
   *
   * Note: We MERGE data instead of clearing first to prevent losing WebSocket updates
   * that may have arrived before the REST load completed. Each record is compared
   * and we keep the one with the better data (e.g., has a time value).
   *
   * IMPORTANT: Uses merge lock to prevent WebSocket updates from racing with this merge.
   * WebSocket messages that arrive during merge are queued and processed after.
   */
  loadTimingData(eventId, timingData) {
    const eventDataObj = this.eventData.events.get(eventId);
    if (!eventDataObj) {
      console.warn(`[DataMerger] Cannot load timing data: event ${eventId} not initialized`);
      return [];
    }

    console.log(`[DataMerger] Loading/merging timing data for event ${eventId}`);

    // Acquire merge lock - WebSocket messages will be queued during merge
    this.acquireMergeLock(eventId);

    try {
      // IMPORTANT: Don't clear existing data - merge instead
      // This prevents losing WebSocket updates that arrived before REST load completed

      // Load startRecords (keyed by entryUuid) - merge with existing data
      if (timingData.startRecords && timingData.startRecords.length > 0) {
        timingData.startRecords.forEach(record => {
          if (record.entryUuid) {
            const existing = eventDataObj.startRecords.get(record.entryUuid);
            if (existing) {
              // Keep record with startTime; if both have it, keep existing (WebSocket is fresher)
              const existingHasTime = existing.startTime != null;
              const newHasTime = record.startTime != null;
              if (newHasTime && !existingHasTime) {
                eventDataObj.startRecords.set(record.entryUuid, record);
              }
              // else keep existing
            } else {
              eventDataObj.startRecords.set(record.entryUuid, record);
            }
          }
        });
      }

      // Load finishRecords (keyed by entryUuid) - merge with existing data.
      // When two records exist for the same entry (chip-data imports can
      // create this), prefer the one with the higher local id. For a tie or
      // same-record match, prefer existing in-memory data since WebSocket
      // updates are fresher than a REST fetch.
      if (timingData.finishRecords && timingData.finishRecords.length > 0) {
        let skipped = 0;
        let merged = 0;
        timingData.finishRecords.forEach(record => {
          if (record.entryUuid) {
            const existing = eventDataObj.finishRecords.get(record.entryUuid);
            if (existing) {
              merged++;
              const newId = record.id ?? 0;
              const existingId = existing.id ?? 0;
              if (newId > existingId) {
                // REST has a newer record than what WebSocket delivered
                eventDataObj.finishRecords.set(record.entryUuid, record);
              }
              // equal or lower id: keep existing (WebSocket is fresher)
            } else {
              eventDataObj.finishRecords.set(record.entryUuid, record);
            }
          } else {
            skipped++;
          }
        });
        // Count how many have finishTime
        let withFinishTime = 0;
        let withoutFinishTime = 0;
        for (const record of eventDataObj.finishRecords.values()) {
          if (record.finishTime) {
            withFinishTime++;
          } else {
            withoutFinishTime++;
          }
        }
        console.log(`[DataMerger] Merged ${timingData.finishRecords.length} finish records, skipped ${skipped} unassigned, ${merged} merged with existing`);
        console.log(`[DataMerger] Finish records with finishTime: ${withFinishTime}, without: ${withoutFinishTime}`);
      }

      // Load intermediateRecords (keyed by entryUuid) - merge with existing data
      if (timingData.intermediateRecords && timingData.intermediateRecords.length > 0) {
        let skipped = 0;
        let merged = 0;
        timingData.intermediateRecords.forEach(record => {
          if (record.entryUuid) {
            const existing = eventDataObj.intermediateRecords.get(record.entryUuid) || [];
            // Check if this record (by UUID) already exists
            const existingIndex = existing.findIndex(r => r.uuid === record.uuid);
            if (existingIndex >= 0) {
              merged++;
              // Record exists - keep the one with time, prefer existing (WebSocket is fresher)
              const existingRecord = existing[existingIndex];
              const existingHasTime = existingRecord.time != null;
              const newHasTime = record.time != null;
              if (newHasTime && !existingHasTime) {
                // Replace existing with new (has time)
                const newArray = [...existing];
                newArray[existingIndex] = record;
                eventDataObj.intermediateRecords.set(record.entryUuid, newArray);
              }
              // else keep existing
            } else {
              // New record - add to array
              eventDataObj.intermediateRecords.set(record.entryUuid, [...existing, record]);
            }
          } else {
            skipped++;
          }
        });
        console.log(`[DataMerger] Merged ${timingData.intermediateRecords.length} intermediate records, skipped ${skipped} unassigned, ${merged} merged with existing`);
      }

      eventDataObj.snapshotProgress.timing = true;

      console.log(`[DataMerger] Timing data loaded:`, {
        starts: eventDataObj.startRecords.size,
        finishes: eventDataObj.finishRecords.size,
        intermediates: eventDataObj.intermediateRecords.size
      });
    } finally {
      // Always release merge lock, even if an error occurs
      // Return any queued messages for processing
      return this.releaseMergeLock(eventId);
    }
  }
}
